import {
  DeployTest,
  DeployTestItem,
  DeployTestItemStatus,
  DeployTestQuestion,
  DeployTestQuestionType,
  DeployTestStatus,
} from '../../types';

export interface FirmwareOption {
  _id: string;
  version: string;
  model: string;
  type?: string;
  releaseNotes: string;
  targetMobileAppVersion?: string;
}

export interface MobileAppOption {
  version: string;
  platform: string;
  channel?: string;
}

export interface TestFormState {
  name: string;
  status: DeployTestStatus;
  firmwareVersion: string;
  firmwareReleaseId: string;
  mobileAppVersion: string;
  mobileAppVersionIos: string;
  mobileAppVersionAndroid: string;
  mobileAppPlatforms: Array<'ios' | 'android'>;
  overview: string;
  releaseNotes: string;
  kickoffEmailEnabled: boolean;
  kickoffEmail: string;
  deployToAllTesters: boolean;
  selectedTesterIds: string[];
  items: DeployTestItem[];
  questions: DeployTestQuestion[];
}

export const STATUS_OPTIONS: { value: DeployTestStatus; label: string }[] = [
  { value: 'planning', label: 'Planning' },
  { value: 'testing', label: 'Testing' },
  { value: 'failed', label: 'Failed' },
  { value: 'succeeded', label: 'Passed' },
  { value: 'aborted', label: 'Aborted' },
];

export const ITEM_STATUS_OPTIONS: { value: DeployTestItemStatus; label: string }[] = [
  { value: 'testing', label: 'Testing' },
  { value: 'passed', label: 'Passed' },
  { value: 'flagged', label: 'Flagged' },
  { value: 'failed', label: 'Failed' },
];

export const SELECT_LATER = '';

export const formatQuestionAnswer = (value?: string, type?: string) => {
  const text = String(value || '').trim();
  if (!text) return '—';
  if (type === 'boolean') {
    if (text === 'yes') return 'Yes';
    if (text === 'no') return 'No';
  }
  return text;
};

export const isFinishedDeployTest = (status?: DeployTestStatus) =>
  status === 'succeeded' || status === 'failed';

export const releaseNoteItems = (value?: string) =>
  String(value || '')
    .split(/\r?\n/)
    .map((line) => line.replace(/^\s*(?:[-*•]|\d+[.)])\s+/, '').trim())
    .filter(Boolean);

export const resolveItemStatus = (item: Pick<DeployTestItem, 'status' | 'done'>): DeployTestItemStatus => {
  if (item.status === 'testing' || item.status === 'passed' || item.status === 'flagged' || item.status === 'failed') {
    return item.status;
  }
  return item.done ? 'passed' : 'testing';
};

export const QUESTION_TYPE_OPTIONS: { value: DeployTestQuestionType; label: string }[] = [
  { value: 'text', label: 'Text' },
  { value: 'boolean', label: 'Yes / No' },
  { value: 'choice', label: 'Multiple choice' },
];

export const emptyItem = (): DeployTestItem => ({ text: '', description: '', status: 'testing' });

export const emptyQuestion = (): DeployTestQuestion => ({
  prompt: '',
  type: 'text',
  choices: [''],
  additionalInfo: '',
});

export const emptyForm = (): TestFormState => ({
  name: '',
  status: 'planning',
  firmwareVersion: '',
  firmwareReleaseId: '',
  mobileAppVersion: '',
  mobileAppVersionIos: '',
  mobileAppVersionAndroid: '',
  mobileAppPlatforms: ['ios', 'android'],
  overview: '',
  releaseNotes: '',
  kickoffEmailEnabled: false,
  kickoffEmail: '',
  deployToAllTesters: true,
  selectedTesterIds: [],
  items: [emptyItem()],
  questions: [],
});

export const formFromTest = (test: DeployTest): TestFormState => ({
  name: test.name,
  status: test.status,
  firmwareVersion: test.firmwareVersion || '',
  firmwareReleaseId: test.firmwareReleaseId || '',
  mobileAppVersion: test.mobileAppVersion || '',
  mobileAppVersionIos: test.mobileAppVersionIos || test.mobileAppVersion || '',
  mobileAppVersionAndroid: test.mobileAppVersionAndroid || test.mobileAppVersion || '',
  mobileAppPlatforms: test.mobileAppPlatforms?.length ? test.mobileAppPlatforms : ['ios', 'android'],
  overview: test.overview || '',
  releaseNotes: test.releaseNotes || '',
  kickoffEmailEnabled: Boolean(test.kickoffEmail?.trim()),
  kickoffEmail: test.kickoffEmail || '',
  deployToAllTesters: test.deployToAllTesters !== false,
  selectedTesterIds: test.selectedTesterIds || [],
  items: test.items?.length
    ? test.items.map((item) => ({
        ...item,
        description: item.description || '',
        status: resolveItemStatus(item),
      }))
    : [emptyItem()],
  questions: (test.questions || []).map((question) => ({
    ...question,
    prompt: question.prompt || '',
    type: question.type || 'text',
    choices: question.choices?.length ? question.choices : [''],
    additionalInfo: question.additionalInfo || '',
  })),
});

export const statusMeta = (status: DeployTestStatus) =>
  STATUS_OPTIONS.find((option) => option.value === status) || STATUS_OPTIONS[0];

export const hasRequiredVersions = (
  test: Pick<DeployTest, 'firmwareVersion' | 'mobileAppVersion'> & {
    mobileAppVersionIos?: string;
    mobileAppVersionAndroid?: string;
  },
) => {
  const fallback = test.mobileAppVersion?.trim();
  return Boolean(
    test.firmwareVersion?.trim()
    && (test.mobileAppVersionIos?.trim() || fallback)
    && (test.mobileAppVersionAndroid?.trim() || fallback)
  );
};

export const hasOutstandingLinearItems = (items?: Array<{ stateType?: string; stateName?: string }>) => (
  (items || []).some((item) => {
    const type = String(item.stateType || '').toLowerCase();
    const name = String(item.stateName || '').toLowerCase();
    return type !== 'completed'
      && type !== 'canceled'
      && name !== 'done'
      && name !== 'canceled'
      && name !== 'cancelled'
      && name !== 'duplicate';
  })
);

export const canStartDeployTest = (
  test: Pick<DeployTest, 'firmwareVersion' | 'mobileAppVersion' | 'firmwareReady'> & {
    mobileAppVersionIos?: string;
    mobileAppVersionAndroid?: string;
  },
) =>
  hasRequiredVersions(test) && test.firmwareReady === true;

export const firmwareTypeLabel = (type?: string) => {
  if (type === 'beta') return 'Beta';
  if (type === 'alpha') return 'Alpha';
  if (type === 'dev') return 'Dev';
  return '';
};

export const slugifyTestName = (name: string) => {
  const slug = String(name || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return slug || 'test';
};

export const planningBlockers = (
  test: DeployTest,
  firmwareNotReady = 'Firmware has not been promoted to beta',
): string[] => {
  const blockers: string[] = [];
  if (!test.firmwareVersion?.trim()) {
    blockers.push('Firmware version has not been selected');
  } else if (test.firmwareReady !== true) {
    blockers.push(firmwareNotReady);
  }
  const fallback = test.mobileAppVersion?.trim();
  if (!(test.mobileAppVersionIos?.trim() || fallback)) {
    blockers.push('iOS mobile app version has not been selected');
  }
  if (!(test.mobileAppVersionAndroid?.trim() || fallback)) {
    blockers.push('Android mobile app version has not been selected');
  }
  return blockers;
};
